import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { debounce } from 'lodash'
import { getBreakpoint } from '@/scripts/tools/constants'
import { getScrollbarWidth } from '@/scripts/tools/utilities'

document.addEventListener('alpine:init', () => {
  Alpine.data('expertises', () => ({
    root: null,
    container: null,
    scrollTriggerInstance: null,

    init() {
      this.$nextTick(() => {
        this.root = this.$el; // Alpine root element passed as context to data()
        this.container = this.$refs.sliderContainer;
        
        // Wait for splash screen to hide (body scroll restored) before calculating widths
        const initWhenReady = () => {
          if (this.root && this.container) {
            this.initScrollTrigger();
          }
        };

        initWhenReady();
      });

      window.addEventListener('resize', debounce(() => {
        if (this.root && this.container) {
          // Force layout recalculation before reinitializing
          void this.container.offsetWidth;
          this.initScrollTrigger();
        }
      }, 250));
    },

    initScrollTrigger() {
      if (window.innerWidth < getBreakpoint('xl')) {
        return;
      }

        // Save current scroll progress if ScrollTrigger exists
        if (this.scrollTriggerInstance) {
          this.scrollTriggerInstance.kill();
          this.scrollTriggerInstance = null;
        }

        // Reset all slides and images to their base state before recalculation
        if (this.root) {
          let items = this.root.querySelectorAll('[data-expertise-slide]');
          items.forEach((slide) => {
            // Reset slide position
            gsap.set(slide, { x: 0, clearProps: 'x' });
            
            // Reset wrapper
            let wrapper = slide.querySelector('[data-expertise-wrapper]');
            if (wrapper) {
              gsap.set(wrapper, { x: 0, clearProps: 'x' });
            }
            
            // Reset image transforms
            let slideImgContainer = slide.querySelector('.text-image__image');
            if (slideImgContainer) {
              gsap.set(slideImgContainer, { clearProps: 'all' });
            }
          });
        }

        // Force layout recalculation
        if (this.container) {
          void this.container.offsetWidth;
        }

        // Invalidate ScrollTrigger to force recalculation
        ScrollTrigger.refresh();

        // Create a new instance
        this.scrollTriggerInstance = this.scrollTrig(this.root, this.container);
        
        // Force immediate update to apply correct positions based on current scroll
        if (this.scrollTriggerInstance) {
          this.scrollTriggerInstance.refresh();
          // Trigger onUpdate manually to recalculate positions
          if (this.scrollTriggerInstance.onUpdate) {
            this.scrollTriggerInstance.onUpdate(this.scrollTriggerInstance);
          }
        }
    },

    scrollTrig(root, container) {
      // Check if root and container are valid
      if (!root || !container || typeof root.querySelectorAll !== 'function') {
        console.warn('scrollTrig: invalid root or container');
        return null;
      }
      gsap.registerPlugin(ScrollTrigger);

      let gsapBl = container ? container.offsetWidth : 0;
      // If odd, round up to the next integer (prevents floating pixels)
      if (gsapBl % 2 === 1) gsapBl = gsapBl + 1;

      // Initial slide positions configuration (in percentage)
      const OFFSET_1 = 320;
      const OFFSET_2 = 122;
      const SLIDE_POSITIONS = {
        0: 0,
        1: ((gsapBl - OFFSET_1) / gsapBl) * 100,
        2: ((gsapBl - OFFSET_2) / gsapBl) * 100,
      };

      /**
       * Dynamically retrieves the width of the card wrapper.
       * Prefers the CSS variable (updated by the ResizeObserver),
       * with a fallback to a direct DOM measurement.
       */
      function getCardWrapperWidth() {
        let width = 0;
        
        const el =
          root.querySelector('[data-expertise-wrapper]') ||
          document.querySelector('[data-expertise-wrapper]');
        if (el) {
          width = el.getBoundingClientRect().width;
        }

        return width;
      }

      /**
       * Calculates the target padding-right based on the current width of the wrapper.
       */
      function getTargetPaddingRight() {
        const cardWrapperWidth = getCardWrapperWidth();

        return (OFFSET_1 + 32) - ((window.innerWidth - getScrollbarWidth() - cardWrapperWidth) / 2);
      }

      /**
       * Calculates and exposes CSS variables for the final container width
       * and the fixed text width (so that text width stays constant).
       */
      function updateSizeCssVariables() {
        const wrapperWidth = getCardWrapperWidth();
        const paddingRight = getTargetPaddingRight();

        // Final container width at the end of the animation
        const finalContainerWidth = wrapperWidth + paddingRight;

        // Content text width: wrapper width minus existing left padding and final right padding
        let finalTextWidth = wrapperWidth;
        const cardWrapperEl = root.querySelector('[data-expertise-wrapper]');
        if (cardWrapperEl) {
          const styles = getComputedStyle(cardWrapperEl);
          const paddingLeft = parseFloat(styles.paddingLeft) || 0;
          finalTextWidth = wrapperWidth - paddingLeft - paddingRight;
        }

        document.documentElement.style.setProperty(
          '--card-expertise-final-container-width',
          `${finalContainerWidth}px`
        );
        document.documentElement.style.setProperty(
          '--card-expertise-text-width',
          `${finalTextWidth}px`
        );
      }

      // Initialize CSS variables based on current sizes
      updateSizeCssVariables();

      // Set each slide to full container width
      const items = root.querySelectorAll('[data-expertise-slide]');
      items.forEach(item => {
        item.style.width = `${gsapBl}px`;
      });

      /**
       * Calculates the initial translation percentage for a given slide
       * @param {number} index - Slide index
       * @returns {number} Translation percentage (0-100)
       */
      function getSlideTranslatePercent(index) {
        return SLIDE_POSITIONS[index] || 0;
      }

      /**
       * Calculates the initial position in pixels for a given slide
       * @param {number} index - Slide index
       * @returns {number} Position in pixels
       */
      function getSlideInitialPosition(index) {
        const translatePercent = getSlideTranslatePercent(index);
        return (translatePercent / 100) * gsapBl;
      }

      /**
       * Calculates the initial position of the next slide
       * @param {number} currentIndex - Current slide index
       * @param {number} totalSlides - Total number of slides
       * @returns {number} Position in pixels of the next slide (or right edge if last)
       */
      function getNextSlidePosition(currentIndex, totalSlides) {
        if (currentIndex < totalSlides - 1) {
          return getSlideInitialPosition(currentIndex + 1);
        }
        // Last slide: until the right edge of the screen
        return gsapBl;
      }

      /**
       * Calculates the visible width of the image (distance between current and next slide)
       * @param {number} currentSlideX - Current position of the slide in pixels
       * @param {number} nextSlidePosition - Position of the next slide in pixels
       * @param {HTMLElement} container - The image container element
       * @returns {number} Visible width in pixels
       */
      function calculateVisibleDimensions(currentSlideX, nextSlidePosition, container) {
        let imageHeight = container.offsetHeight || 0;
        let imageWidth = container.offsetWidth || 0;
        let aspectRatio = imageHeight > 0 ? imageWidth / imageHeight : 1;
        let calculatedWidth = Math.max(0, nextSlidePosition - currentSlideX);
        let calculatedHeight = aspectRatio > 0 ? calculatedWidth / aspectRatio : 0;

        return {
          width: calculatedWidth,
          height: calculatedHeight
        };
      }

      /**
       * Calculates adjusted x and y positions accounting for scale transformation origin (center)
       * @param {number} targetX - Target x position relative to initial position
       * @param {number} targetY - Target y position relative to initial position
       * @param {number} originalWidth - Original image width
       * @param {number} originalHeight - Original image height
       * @param {number} scale - Current scale value
       * @returns {{x: number, y: number}} Adjusted positions
       */
      function calculateAdjustedPositions(targetX, targetY, originalWidth, originalHeight, scale) {
        let scaledWidth = originalWidth * scale;
        let scaledHeight = originalHeight * scale;
        let widthDifference = originalWidth - scaledWidth;
        let heightDifference = originalHeight - scaledHeight;
        
        return {
          x: targetX - (widthDifference / 2),
          y: targetY - (heightDifference / 2)
        };
      }

      // Calculate initial positions for each slide based on CSS percentages
      let initialPositions = [];
      let initialImagePositions = [];
      let targetImagePositions = [];
      let originalImageWidths = [];
      let originalImageHeights = [];
      let initialScales = [];

      // Force recalculation of container width after potential resize
      gsapBl = container ? container.offsetWidth : 0;

      items.forEach((slide, index) => {
        let slideImgContainer = slide.querySelector('.text-image__image');
        
        // Initial slide position
        let initialPosition = getSlideInitialPosition(index);
        initialPositions.push(initialPosition);

        // Ensure slide is at position 0 before calculating image positions
        gsap.set(slide, { x: 0 });
        
        // Reset wrapper to 0
        let wrapper = slide.querySelector('[data-expertise-wrapper]');
        if (wrapper) {
          gsap.set(wrapper, { x: 0 });
        }

        // Reset image transforms completely before recalculating positions
        if (slideImgContainer) {
          gsap.set(slideImgContainer, { clearProps: 'all' });
          // Force browser to recalculate layout
          void slideImgContainer.offsetWidth;
        }

        // Get the initial position of the image (when slide is at x:0)
        if (slideImgContainer && index > 0) {
          // Force layout recalculation to ensure we get correct positions
          void slideImgContainer.offsetWidth;
          void slide.offsetWidth;
          
          // Get positions relative to the slide (which is now at x:0)
          let rect = slideImgContainer.getBoundingClientRect();
          let slideRect = slide.getBoundingClientRect();
          let initialLeft = rect.left - slideRect.left;
          let initialTop = rect.top - slideRect.top;
          initialImagePositions.push({
            left: initialLeft,
            top: initialTop
          });

          // Calculate the target position for the image
          // The right edge of the image must align with the left edge of the next slide
          let nextSlidePosition = getNextSlidePosition(index, items.length);
          let imageWidth = slideImgContainer.offsetWidth || 0;
          let imageHeight = slideImgContainer.offsetHeight || 0;
          
          // Store original image dimensions for scale calculation
          originalImageWidths[index] = imageWidth;
          originalImageHeights[index] = imageHeight;
          
          // Calculate initial scale to adjust target position
          let initialWidthSlideVisible = Math.ceil(calculateVisibleDimensions(initialPosition, nextSlidePosition, slideImgContainer).width);
          let initialScale = imageWidth > 0 ? initialWidthSlideVisible / imageWidth : 1;
          initialScales[index] = initialScale;
          
          // Horizontal position: left edge of the image = right edge of next slide - scaled image width
          // Position relative to the current slide
          // With scale, the actual width is imageWidth * initialScale
          let scaledImageWidth = imageWidth * initialScale;
          let targetLeft = nextSlidePosition - scaledImageWidth;
          
          // Get the value of --card-expertise-padding-bottom from the slide or document
          let style = window.getComputedStyle(slide);
          let paddingBottomStr = style.getPropertyValue('--card-expertise-padding-bottom') || "0";
          let paddingBottom = parseFloat(paddingBottomStr);
          if (isNaN(paddingBottom)) paddingBottom = 0;

          let targetTop = slideRect.height - calculateVisibleDimensions(initialPosition, nextSlidePosition, slideImgContainer).height - paddingBottom;

          slide.style.setProperty('--target-left', targetLeft);
          slide.style.setProperty('--target-top', targetTop);
          
          targetImagePositions.push({
            left: targetLeft,
            top: targetTop
          });
        } else {
          initialImagePositions.push({ left: 0, top: 0 });
          targetImagePositions.push({ left: 0, top: 0 });
          originalImageWidths[index] = 0;
          originalImageHeights[index] = 0;
          initialScales[index] = 1;
        }

        // Now set the slide to its initial position
        gsap.set(slide, { x: initialPosition });
        
        // Initialize wrapper to stay fixed at left of screen
        if (wrapper) {
          // Compensate slide position to keep wrapper visually fixed at left
          gsap.set(wrapper, { x: -initialPosition });

          if (index === 0) {
            const initialTargetPaddingRight = getTargetPaddingRight();
            wrapper.style.paddingRight = initialTargetPaddingRight + 'px';
          }
        }
      });

      /**
       * Initializes an image to its target position
       * @param {HTMLElement} slideImgContainer - The image container
       * @param {HTMLElement} slide - The slide element
       * @param {number} index - Slide index
       */
      function initializeImage(slideImgContainer, slide, index) {
        let initialImgPos = initialImagePositions[index];
        let targetImgPos = targetImagePositions[index];
        let initialPosition = initialPositions[index];
        
        // Get already calculated initial scale
        let initialScale = initialScales[index] || 1;
        let originalWidth = originalImageWidths[index] || 1;

        // Calculate currentLeft at initialization (slideProgress = 0, so currentLeft = targetImgPos.left)
        let currentLeft = targetImgPos.left;
        // At initialization, currentX = initialPosition (slide hasn't moved yet)
        let currentX = initialPosition;
        let nextSlidePosition = getNextSlidePosition(index, items.length);
        let widthSlideVisible = Math.ceil(calculateVisibleDimensions(currentX, nextSlidePosition, slideImgContainer).width);

        // Adjust x and y positions to account for scale transformation origin (center)
        let originalHeight = originalImageHeights[index] || 1;
        let targetX = targetImgPos.left - initialImgPos.left;
        let targetY = targetImgPos.top - initialImgPos.top;
        let adjustedPositions = calculateAdjustedPositions(targetX, targetY, originalWidth, originalHeight, initialScale);

        gsap.set(slideImgContainer, {
          x: adjustedPositions.x,
          y: adjustedPositions.y,
          scale: initialScale,
        });

        // Set CSS variables on the slide element for availability from initialization
        slide.style.setProperty('--current-left', currentLeft);
        slide.style.setProperty('--width-slide-visible', widthSlideVisible);
      }

      /**
       * Applies the line-height of data-expertise-heading to the ::after of each slide
       * @param {NodeList} items - List of slides
       */
      function applyHeadingLineHeight(items) {
        items.forEach((slide, index) => {
          if (index > 0) { // Only for slides that are not the first
            let heading = slide.querySelector('[data-expertise-heading]');
            if (heading) {
              const computedStyle = window.getComputedStyle(heading);
              const lineHeight = computedStyle.lineHeight;
              // Apply the line-height as the height of ::after via a CSS variable
              slide.style.setProperty('--after-height', lineHeight);
            }
          }
        });
      }

      // Initialize each image at its target position (start of progress)
      items.forEach((slide, index) => {
        let slideImgContainer = slide.querySelector('.text-image__image');
        if (slideImgContainer && initialImagePositions[index] && targetImagePositions[index] && index > 0) {
          initializeImage(slideImgContainer, slide, index);
        }
      });

      // Read the line-height of data-expertise-heading and apply it to ::after
      applyHeadingLineHeight(items);

      // Each slide needs enough scroll distance to travel from its position to 0
      // Use container width as the scroll distance per slide
      // Animation duration factor: lower value = shorter animation (less scroll needed)
      const ANIMATION_DURATION_FACTOR = 0.4;
      let scrollDistancePerSlide = gsapBl * ANIMATION_DURATION_FACTOR;
      let totalScrollDistance = scrollDistancePerSlide * items.length;

      // Create a single ScrollTrigger that handles pinning and all slide animations
      let scrollTrigger = ScrollTrigger.create({
        trigger: root,
        start: 'top top',
        end: () => '+=' + totalScrollDistance,
        pin: true,
        pinSpacing: true,
        scrub: true,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          // Calculate overall scroll progress (0 to 1)
          let overallProgress = self.progress;
          
        /**
        * Calculates the progress of an individual slide based on global progress.
        * The first slide (index 0) is static and not part of the animation timeline.
        * The animation starts at slide 1.
        * @param {number} index - Slide index
        * @returns {number} Slide progress (0 to 1)
        */
        function calculateSlideProgress(index) {
          // Slide 0 is not animated
          if (index === 0) {
            return 0;
          }

          // Number of animated slides (all except the first one)
          const animatedSlidesCount = Math.max(items.length - 1, 1);
          // Rebase index so that slide 1 -> 0, slide 2 -> 1, etc.
          const animatedIndex = index - 1;

          let slideStartProgress = animatedIndex / animatedSlidesCount;
          let slideEndProgress = (animatedIndex + 1) / animatedSlidesCount;
          
          if (overallProgress >= slideStartProgress && overallProgress <= slideEndProgress) {
            // Normalize progress to 0-1 for this slide's section
            return (overallProgress - slideStartProgress) / (slideEndProgress - slideStartProgress);
          } else if (overallProgress > slideEndProgress) {
            return 1; // Slide has finished, keep it at left
          }
          return 0; // Slide hasn't started
        }

        /**
         * Updates CSS classes according to animation state
         * @param {HTMLElement} slide - Slide element
         * @param {number} slideProgress - Slide progress (0 to 1)
         */
        function updateAnimationClasses(slide, slideProgress) {
          if (slideProgress === 1) {
            slide.classList.add('end-animation');
            slide.classList.remove('animated');
          } else if (slideProgress > 0) {
            slide.classList.add('animated');
            slide.classList.remove('end-animation');
          } else {
            slide.classList.remove('animated');
            slide.classList.remove('end-animation');
          }
        }

        /**
         * Animates a slide and its image
         * @param {HTMLElement} slide - Slide element
         * @param {number} index - Slide index
         */
        function animateSlide(slide, index) {
          let initialPosition = initialPositions[index];
          let slideProgress = calculateSlideProgress(index);
          
          // Current slide position: interpolate from initialPosition to 0
          let currentX = initialPosition * (1 - slideProgress);
          
          // Apply transformation to the slide
          gsap.set(slide, { x: currentX });

          // Animate the wrapper to stay fixed at left of screen
          // Compensate the slide movement by moving wrapper in opposite direction
          let wrapper = slide.querySelector('[data-expertise-wrapper]');
          if (wrapper) {
            // Move wrapper in opposite direction to keep it visually fixed at left
            let wrapperX = -currentX;
            gsap.set(wrapper, { x: wrapperX });
            
            // For the other slides, apply a progressive padding toward the target value
            if (index > 0) {
              const targetPaddingRight = getTargetPaddingRight();
              // Get initial padding-right from CSS (remove inline style temporarily to get base value)
              let inlineStyle = wrapper.style.paddingRight;
              wrapper.style.paddingRight = '';
              let initialPaddingRight = parseFloat(getComputedStyle(wrapper).paddingRight) || 0;
              wrapper.style.paddingRight = inlineStyle;
              
              // Calculate progressive padding
              let progressivePadding = targetPaddingRight * slideProgress;
              
              // Apply the greater value between initial and progressive
              if (progressivePadding > initialPaddingRight) {
                wrapper.style.paddingRight = progressivePadding + 'px';
              } else {
                wrapper.style.paddingRight = '';
              }
            }
          }

          // Animate the image if it exists
          let slideImgContainer = slide.querySelector('.text-image__image');
          if (slideImgContainer && initialImagePositions[index] && targetImagePositions[index]) {
            let initialImgPos = initialImagePositions[index];
            let targetImgPos = targetImagePositions[index];
            
            // Interpolate from target position (0%) to initial (100%)
            // slideProgress 0 = target, slideProgress 1 = initial
            let currentLeft = targetImgPos.left + (initialImgPos.left - targetImgPos.left) * slideProgress;
            let currentTop = targetImgPos.top + (initialImgPos.top - targetImgPos.top) * slideProgress;
            
            // Calculate visible width: distance between current slide position and next slide
            let nextSlidePosition = getNextSlidePosition(index, items.length);
            let widthSlideVisible = Math.ceil(calculateVisibleDimensions(currentX, nextSlidePosition, slideImgContainer).width);
            
            // Interpolate scale from initial scale to 1
            // slideProgress 0 = initialScale, slideProgress 1 = 1
            let initialScale = initialScales[index] || 1;
            let scale = initialScale + (1 - initialScale) * slideProgress;
            
            // Adjust x and y positions to account for scale transformation origin (center)
            let originalWidth = originalImageWidths[index] || 1;
            let originalHeight = originalImageHeights[index] || 1;
            let targetX = currentLeft - initialImgPos.left;
            let targetY = currentTop - initialImgPos.top;
            let adjustedPositions = calculateAdjustedPositions(targetX, targetY, originalWidth, originalHeight, scale);
                          
            // Apply transformations to the image
            gsap.set(slideImgContainer, {
              x: adjustedPositions.x,
              y: adjustedPositions.y,
              scale: scale,
            });

            slide.style.setProperty('--current-left', currentLeft);
            slide.style.setProperty('--width-slide-visible', widthSlideVisible);
            // Update animation classes
            updateAnimationClasses(slide, slideProgress);
          }
        }
        
        // Animate each slide independently based on overall progress
        items.forEach((slide, index) => {
          animateSlide(slide, index);
        });
        },
      });
      
      // Force immediate update to apply correct positions
      if (scrollTrigger) {
        scrollTrigger.refresh();
      }
      
      return scrollTrigger;
    }
  }))
});
